import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private current = new BehaviorSubject<string>('en');
  public locale$ = this.current.asObservable();
  private loadedLocales = new Set<string>();

  // Comprehensive locale mapping based on actual Angular locales
  private readonly LOCALE_MAPPING: { [key: string]: string } = {
    // English variants
    'en': 'en', 'en-001': 'en-001', 'en-150': 'en-150', 'en-AE': 'en-AE', 'en-AG': 'en-AG', 'en-AI': 'en-AI',
    'en-AS': 'en-AS', 'en-AT': 'en-AT', 'en-AU': 'en-AU', 'en-BB': 'en-BB', 'en-BE': 'en-BE', 'en-BI': 'en-BI',
    'en-BM': 'en-BM', 'en-BS': 'en-BS', 'en-BW': 'en-BW', 'en-BZ': 'en-BZ', 'en-CA': 'en-CA', 'en-CC': 'en-CC',
    'en-CH': 'en-CH', 'en-CK': 'en-CK', 'en-CM': 'en-CM', 'en-CX': 'en-CX', 'en-CY': 'en-CY', 'en-DE': 'en-DE',
    'en-DG': 'en-DG', 'en-DK': 'en-DK', 'en-DM': 'en-DM', 'en-ER': 'en-ER', 'en-FI': 'en-FI', 'en-FJ': 'en-FJ',
    'en-FK': 'en-FK', 'en-FM': 'en-FM', 'en-GB': 'en-GB', 'en-GD': 'en-GD', 'en-GG': 'en-GG', 'en-GH': 'en-GH',
    'en-GI': 'en-GI', 'en-GM': 'en-GM', 'en-GU': 'en-GU', 'en-GY': 'en-GY', 'en-HK': 'en-HK', 'en-IE': 'en-IE',
    'en-IL': 'en-IL', 'en-IM': 'en-IM', 'en-IN': 'en-IN', 'en-IO': 'en-IO', 'en-JE': 'en-JE', 'en-JM': 'en-JM',
    'en-KE': 'en-KE', 'en-KI': 'en-KI', 'en-KN': 'en-KN', 'en-KY': 'en-KY', 'en-LC': 'en-LC', 'en-LR': 'en-LR',
    'en-LS': 'en-LS', 'en-MG': 'en-MG', 'en-MH': 'en-MH', 'en-MO': 'en-MO', 'en-MP': 'en-MP', 'en-MS': 'en-MS',
    'en-MT': 'en-MT', 'en-MU': 'en-MU', 'en-MV': 'en-MV', 'en-MW': 'en-MW', 'en-MY': 'en-MY', 'en-NA': 'en-NA',
    'en-NF': 'en-NF', 'en-NG': 'en-NG', 'en-NL': 'en-NL', 'en-NR': 'en-NR', 'en-NU': 'en-NU', 'en-NZ': 'en-NZ',
    'en-PG': 'en-PG', 'en-PH': 'en-PH', 'en-PK': 'en-PK', 'en-PN': 'en-PN', 'en-PR': 'en-PR', 'en-PW': 'en-PW',
    'en-RW': 'en-RW', 'en-SB': 'en-SB', 'en-SC': 'en-SC', 'en-SD': 'en-SD', 'en-SE': 'en-SE', 'en-SG': 'en-SG',
    'en-SH': 'en-SH', 'en-SI': 'en-SI', 'en-SL': 'en-SL', 'en-SS': 'en-SS', 'en-SX': 'en-SX', 'en-SZ': 'en-SZ',
    'en-TC': 'en-TC', 'en-TK': 'en-TK', 'en-TO': 'en-TO', 'en-TT': 'en-TT', 'en-TV': 'en-TV', 'en-TZ': 'en-TZ',
    'en-UG': 'en-UG', 'en-UM': 'en-UM', 'en-VC': 'en-VC', 'en-VG': 'en-VG', 'en-VI': 'en-VI', 'en-VU': 'en-VU',
    'en-WS': 'en-WS', 'en-ZA': 'en-ZA', 'en-ZM': 'en-ZM', 'en-ZW': 'en-ZW',

    // Spanish variants
    'es': 'es', 'es-419': 'es-419', 'es-AR': 'es-AR', 'es-BO': 'es-BO', 'es-BR': 'es-BR', 'es-BZ': 'es-BZ',
    'es-CL': 'es-CL', 'es-CO': 'es-CO', 'es-CR': 'es-CR', 'es-CU': 'es-CU', 'es-DO': 'es-DO', 'es-EA': 'es-EA',
    'es-EC': 'es-EC', 'es-GQ': 'es-GQ', 'es-GT': 'es-GT', 'es-HN': 'es-HN', 'es-IC': 'es-IC', 'es-MX': 'es-MX',
    'es-NI': 'es-NI', 'es-PA': 'es-PA', 'es-PE': 'es-PE', 'es-PH': 'es-PH', 'es-PR': 'es-PR', 'es-PY': 'es-PY',
    'es-SV': 'es-SV', 'es-US': 'es-US', 'es-UY': 'es-UY', 'es-VE': 'es-VE',

    // French variants
    'fr': 'fr', 'fr-BE': 'fr-BE', 'fr-BF': 'fr-BF', 'fr-BI': 'fr-BI', 'fr-BJ': 'fr-BJ', 'fr-BL': 'fr-BL',
    'fr-CA': 'fr-CA', 'fr-CD': 'fr-CD', 'fr-CF': 'fr-CF', 'fr-CG': 'fr-CG', 'fr-CH': 'fr-CH', 'fr-CI': 'fr-CI',
    'fr-CM': 'fr-CM', 'fr-DJ': 'fr-DJ', 'fr-DZ': 'fr-DZ', 'fr-GA': 'fr-GA', 'fr-GF': 'fr-GF', 'fr-GN': 'fr-GN',
    'fr-GP': 'fr-GP', 'fr-GQ': 'fr-GQ', 'fr-HT': 'fr-HT', 'fr-KM': 'fr-KM', 'fr-LU': 'fr-LU', 'fr-MA': 'fr-MA',
    'fr-MC': 'fr-MC', 'fr-MF': 'fr-MF', 'fr-MG': 'fr-MG', 'fr-ML': 'fr-ML', 'fr-MQ': 'fr-MQ', 'fr-MR': 'fr-MR',
    'fr-MU': 'fr-MU', 'fr-NC': 'fr-NC', 'fr-NE': 'fr-NE', 'fr-PF': 'fr-PF', 'fr-PM': 'fr-PM', 'fr-RE': 'fr-RE',
    'fr-RW': 'fr-RW', 'fr-SC': 'fr-SC', 'fr-SN': 'fr-SN', 'fr-SY': 'fr-SY', 'fr-TD': 'fr-TD', 'fr-TG': 'fr-TG',
    'fr-TN': 'fr-TN', 'fr-VU': 'fr-VU', 'fr-WF': 'fr-WF', 'fr-YT': 'fr-YT',

    // German variants
    'de': 'de', 'de-AT': 'de-AT', 'de-BE': 'de-BE', 'de-CH': 'de-CH', 'de-IT': 'de-IT', 'de-LI': 'de-LI',
    'de-LU': 'de-LU',

    // Portuguese variants
    'pt': 'pt', 'pt-AO': 'pt-AO', 'pt-CH': 'pt-CH', 'pt-CV': 'pt-CV', 'pt-GQ': 'pt-GQ', 'pt-GW': 'pt-GW',
    'pt-LU': 'pt-LU', 'pt-MO': 'pt-MO', 'pt-MZ': 'pt-MZ', 'pt-PT': 'pt-PT', 'pt-ST': 'pt-ST', 'pt-TL': 'pt-TL',

    // Italian variants
    'it': 'it', 'it-CH': 'it-CH', 'it-SM': 'it-SM', 'it-VA': 'it-VA',

    // Russian variants
    'ru': 'ru', 'ru-BY': 'ru-BY', 'ru-KG': 'ru-KG', 'ru-KZ': 'ru-KZ', 'ru-MD': 'ru-MD', 'ru-UA': 'ru-UA',

    // Dutch variants
    'nl': 'nl', 'nl-AW': 'nl-AW', 'nl-BE': 'nl-BE', 'nl-BQ': 'nl-BQ', 'nl-CW': 'nl-CW', 'nl-SR': 'nl-SR',
    'nl-SX': 'nl-SX',

    // Chinese variants
    'zh': 'zh', 'zh-Hans': 'zh-Hans', 'zh-Hans-HK': 'zh-Hans-HK', 'zh-Hans-MO': 'zh-Hans-MO',
    'zh-Hans-SG': 'zh-Hans-SG', 'zh-Hant': 'zh-Hant', 'zh-Hant-HK': 'zh-Hant-HK', 'zh-Hant-MO': 'zh-Hant-MO',
    'yue': 'yue', 'yue-Hans': 'yue-Hans', 'yue-Hant': 'yue-Hant',

    // Japanese and Korean
    'ja': 'ja', 'ko': 'ko', 'ko-KP': 'ko-KP',

    // Arabic variants
    'ar': 'ar', 'ar-AE': 'ar-AE', 'ar-BH': 'ar-BH', 'ar-DJ': 'ar-DJ', 'ar-DZ': 'ar-DZ', 'ar-EG': 'ar-EG',
    'ar-EH': 'ar-EH', 'ar-ER': 'ar-ER', 'ar-IL': 'ar-IL', 'ar-IQ': 'ar-IQ', 'ar-JO': 'ar-JO', 'ar-KM': 'ar-KM',
    'ar-KW': 'ar-KW', 'ar-LB': 'ar-LB', 'ar-LY': 'ar-LY', 'ar-MA': 'ar-MA', 'ar-MR': 'ar-MR', 'ar-OM': 'ar-OM',
    'ar-PS': 'ar-PS', 'ar-QA': 'ar-QA', 'ar-SA': 'ar-SA', 'ar-SD': 'ar-SD', 'ar-SO': 'ar-SO', 'ar-SS': 'ar-SS',
    'ar-SY': 'ar-SY', 'ar-TD': 'ar-TD', 'ar-TN': 'ar-TN', 'ar-YE': 'ar-YE',

    // Other major languages
    'pl': 'pl', 'cs': 'cs', 'da': 'da', 'da-GL': 'da-GL', 'fi': 'fi', 'sv': 'sv', 'sv-AX': 'sv-AX', 'sv-FI': 'sv-FI',
    'nb': 'nb', 'nb-SJ': 'nb-SJ', 'nn': 'nn', 'no': 'no', 'hu': 'hu', 'ro': 'ro', 'ro-MD': 'ro-MD', 'bg': 'bg',
    'hr': 'hr', 'hr-BA': 'hr-BA', 'sk': 'sk', 'sl': 'sl', 'et': 'et', 'lv': 'lv', 'lt': 'lt', 'el': 'el',
    'el-CY': 'el-CY', 'he': 'he', 'hi': 'hi', 'hi-Latn': 'hi-Latn', 'th': 'th', 'vi': 'vi', 'id': 'id',
    'ms': 'ms', 'ms-BN': 'ms-BN', 'ms-ID': 'ms-ID', 'ms-SG': 'ms-SG', 'tr': 'tr', 'tr-CY': 'tr-CY', 'uk': 'uk',
    'be': 'be', 'be-tarask': 'be-tarask', 'sr': 'sr', 'sr-Cyrl': 'sr-Cyrl', 'sr-Cyrl-BA': 'sr-Cyrl-BA',
    'sr-Cyrl-ME': 'sr-Cyrl-ME', 'sr-Cyrl-XK': 'sr-Cyrl-XK', 'sr-Latn': 'sr-Latn', 'sr-Latn-BA': 'sr-Latn-BA',
    'sr-Latn-ME': 'sr-Latn-ME', 'sr-Latn-XK': 'sr-Latn-XK', 'mk': 'mk', 'sq': 'sq', 'sq-MK': 'sq-MK',
    'sq-XK': 'sq-XK', 'bs': 'bs', 'bs-Cyrl': 'bs-Cyrl', 'bs-Latn': 'bs-Latn', 'ca': 'ca', 'ca-AD': 'ca-AD',
    'ca-ES-valencia': 'ca-ES-valencia', 'ca-FR': 'ca-FR', 'ca-IT': 'ca-IT', 'cy': 'cy', 'ga': 'ga',
    'ga-GB': 'ga-GB', 'mt': 'mt', 'is': 'is', 'fo': 'fo', 'fo-DK': 'fo-DK', 'gl': 'gl', 'eu': 'eu',
    'af': 'af', 'af-NA': 'af-NA', 'zu': 'zu', 'xh': 'xh', 'sw': 'sw', 'sw-CD': 'sw-CD', 'sw-KE': 'sw-KE',
    'sw-UG': 'sw-UG', 'rw': 'rw', 'lg': 'lg', 'ak': 'ak', 'am': 'am', 'as': 'as', 'bn': 'bn', 'bn-IN': 'bn-IN',
    'bo': 'bo', 'bo-IN': 'bo-IN', 'dz': 'dz', 'gu': 'gu', 'kn': 'kn', 'ml': 'ml', 'mr': 'mr', 'ne': 'ne',
    'ne-IN': 'ne-IN', 'or': 'or', 'pa': 'pa', 'pa-Arab': 'pa-Arab', 'pa-Guru': 'pa-Guru', 'si': 'si',
    'ta': 'ta', 'ta-LK': 'ta-LK', 'ta-MY': 'ta-MY', 'ta-SG': 'ta-SG', 'te': 'te', 'ur': 'ur', 'ur-IN': 'ur-IN',
    'fa': 'fa', 'fa-AF': 'fa-AF', 'ps': 'ps', 'ps-PK': 'ps-PK', 'ku': 'ku', 'ckb': 'ckb', 'ckb-IR': 'ckb-IR',
    'se': 'se', 'se-FI': 'se-FI', 'se-SE': 'se-SE',

    // Additional languages from the available list
    'agq': 'agq', 'asa': 'asa', 'ast': 'ast', 'az': 'az', 'az-Cyrl': 'az-Cyrl', 'az-Latn': 'az-Latn',
    'bas': 'bas', 'bem': 'bem', 'bez': 'bez', 'bm': 'bm', 'br': 'br', 'brx': 'brx', 'ccp': 'ccp', 'ccp-IN': 'ccp-IN',
    'ce': 'ce', 'ceb': 'ceb', 'cgg': 'cgg', 'chr': 'chr', 'dav': 'dav', 'dje': 'dje', 'doi': 'doi', 'dsb': 'dsb',
    'dua': 'dua', 'dyo': 'dyo', 'ebu': 'ebu', 'ee': 'ee', 'ee-TG': 'ee-TG', 'eo': 'eo', 'ewo': 'ewo', 'ff': 'ff',
    'ff-Adlm': 'ff-Adlm', 'ff-Adlm-BF': 'ff-Adlm-BF', 'ff-Adlm-CM': 'ff-Adlm-CM', 'ff-Adlm-GH': 'ff-Adlm-GH',
    'ff-Adlm-GM': 'ff-Adlm-GM', 'ff-Adlm-GW': 'ff-Adlm-GW', 'ff-Adlm-LR': 'ff-Adlm-LR', 'ff-Adlm-MR': 'ff-Adlm-MR',
    'ff-Adlm-NE': 'ff-Adlm-NE', 'ff-Adlm-NG': 'ff-Adlm-NG', 'ff-Adlm-SL': 'ff-Adlm-SL', 'ff-Adlm-SN': 'ff-Adlm-SN',
    'ff-CM': 'ff-CM', 'ff-GN': 'ff-GN', 'ff-Latn': 'ff-Latn', 'ff-Latn-BF': 'ff-Latn-BF', 'ff-Latn-CM': 'ff-Latn-CM',
    'ff-Latn-GH': 'ff-Latn-GH', 'ff-Latn-GM': 'ff-Latn-GM', 'ff-Latn-GN': 'ff-Latn-GN', 'ff-Latn-GW': 'ff-Latn-GW',
    'ff-Latn-LR': 'ff-Latn-LR', 'ff-Latn-MR': 'ff-Latn-MR', 'ff-Latn-NE': 'ff-Latn-NE', 'ff-Latn-NG': 'ff-Latn-NG',
    'ff-Latn-SL': 'ff-Latn-SL', 'ff-MR': 'ff-MR', 'fil': 'fil', 'fur': 'fur', 'fy': 'fy', 'gd': 'gd', 'gsw': 'gsw',
    'gsw-FR': 'gsw-FR', 'gsw-LI': 'gsw-LI', 'guz': 'guz', 'gv': 'gv', 'ha': 'ha', 'ha-GH': 'ha-GH', 'ha-NE': 'ha-NE',
    'haw': 'haw', 'hsb': 'hsb', 'hy': 'hy', 'ia': 'ia', 'ig': 'ig', 'ii': 'ii', 'jgo': 'jgo', 'jmc': 'jmc',
    'jv': 'jv', 'ka': 'ka', 'kab': 'kab', 'kam': 'kam', 'kde': 'kde', 'kea': 'kea', 'kgp': 'kgp', 'khq': 'khq',
    'ki': 'ki', 'kk': 'kk', 'kkj': 'kkj', 'kl': 'kl', 'kln': 'kln', 'km': 'km', 'kok': 'kok', 'ks': 'ks',
    'ks-Arab': 'ks-Arab', 'ks-Deva': 'ks-Deva', 'ksb': 'ksb', 'ksf': 'ksf', 'ksh': 'ksh', 'kw': 'kw', 'ky': 'ky',
    'lag': 'lag', 'lb': 'lb', 'lkt': 'lkt', 'ln': 'ln', 'ln-AO': 'ln-AO', 'ln-CF': 'ln-CF', 'ln-CG': 'ln-CG',
    'lo': 'lo', 'lrc': 'lrc', 'lrc-IQ': 'lrc-IQ', 'lu': 'lu', 'luo': 'luo', 'luy': 'luy', 'mai': 'mai',
    'mas': 'mas', 'mas-TZ': 'mas-TZ', 'mer': 'mer', 'mfe': 'mfe', 'mg': 'mg', 'mgh': 'mgh', 'mgo': 'mgo',
    'mi': 'mi', 'mn': 'mn', 'mni': 'mni', 'mni-Beng': 'mni-Beng', 'mua': 'mua', 'my': 'my', 'mzn': 'mzn',
    'naq': 'naq', 'nd': 'nd', 'nds': 'nds', 'nds-NL': 'nds-NL', 'nmg': 'nmg', 'nnh': 'nnh', 'nus': 'nus',
    'nyn': 'nyn', 'om': 'om', 'om-KE': 'om-KE', 'os': 'os', 'os-RU': 'os-RU', 'pcm': 'pcm', 'qu': 'qu',
    'qu-BO': 'qu-BO', 'qu-EC': 'qu-EC', 'rm': 'rm', 'rn': 'rn', 'rof': 'rof', 'rwk': 'rwk', 'sa': 'sa',
    'sah': 'sah', 'saq': 'saq', 'sat': 'sat', 'sat-Olck': 'sat-Olck', 'sbp': 'sbp', 'sc': 'sc', 'sd': 'sd',
    'sd-Arab': 'sd-Arab', 'sd-Deva': 'sd-Deva', 'seh': 'seh', 'ses': 'ses', 'sg': 'sg', 'shi': 'shi',
    'shi-Latn': 'shi-Latn', 'shi-Tfng': 'shi-Tfng', 'smn': 'smn', 'sn': 'sn', 'so': 'so', 'so-DJ': 'so-DJ',
    'so-ET': 'so-ET', 'so-KE': 'so-KE', 'su': 'su', 'su-Latn': 'su-Latn', 'teo': 'teo', 'teo-KE': 'teo-KE',
    'tg': 'tg', 'ti': 'ti', 'ti-ER': 'ti-ER', 'tk': 'tk', 'to': 'to', 'tt': 'tt', 'twq': 'twq', 'tzm': 'tzm',
    'ug': 'ug', 'und': 'und', 'uz': 'uz', 'uz-Arab': 'uz-Arab', 'uz-Cyrl': 'uz-Cyrl', 'uz-Latn': 'uz-Latn',
    'vai': 'vai', 'vai-Latn': 'vai-Latn', 'vai-Vaii': 'vai-Vaii', 'vun': 'vun', 'wae': 'wae', 'wo': 'wo',
    'xog': 'xog', 'yav': 'yav', 'yi': 'yi', 'yo': 'yo', 'yo-BJ': 'yo-BJ', 'yrl': 'yrl', 'yrl-CO': 'yrl-CO',
    'yrl-VE': 'yrl-VE', 'zgh': 'zgh'
  };

  constructor(@Inject(DOCUMENT) private document: any) {
    // Initialize with default locale
    this.initializeDefaultLocale();
  }

  /**
   * Initialize the service with default locale
   */
  private async initializeDefaultLocale(): Promise<void> {
    try {
      // Test if we can load the default locale
      const success = await this.loadLocaleData('en');
      if (success) {
        console.log('Default locale (en) loaded successfully');
      } else {
        console.error('Failed to load default locale (en)');
      }
    } catch (error) {
      console.error('Error initializing default locale:', error);
    }
  }

  /**
   * Get the Angular locale code for a given locale
   */
  private getAngularLocale(locale: string): string {
    return this.LOCALE_MAPPING[locale] || locale;
  }

  /**
   * Get fallback locale if the requested locale is not available
   */
  private getFallbackLocale(locale: string): string {
    // Try to get base language
    const baseLanguage = locale.split('-')[0];
    if (baseLanguage !== locale) {
      return baseLanguage;
    }

    // Default to English
    return 'en';
  }

  /**
   * Test method to verify locale loading
   */
  async testLocaleLoading(locale: string): Promise<boolean> {
    console.log(`Testing locale loading for: ${locale}`);

    try {
      // Use the same switch-based approach as loadLocaleData
      let data;
      switch (locale) {
        case 'en':
          data = await import('@angular/common/locales/en');
          break;
        case 'de':
          data = await import('@angular/common/locales/de');
          break;
        case 'fr':
          data = await import('@angular/common/locales/fr');
          break;
        case 'es':
          data = await import('@angular/common/locales/es');
          break;
        case 'it':
          data = await import('@angular/common/locales/it');
          break;
        case 'pt':
          data = await import('@angular/common/locales/pt');
          break;
        case 'ru':
          data = await import('@angular/common/locales/ru');
          break;
        case 'zh':
          data = await import('@angular/common/locales/zh');
          break;
        case 'ja':
          data = await import('@angular/common/locales/ja');
          break;
        case 'ko':
          data = await import('@angular/common/locales/ko');
          break;
        default:
          throw new Error(`Locale ${locale} not supported in test method`);
      }

      console.log(`Import successful for ${locale}:`, data);
      return true;
    } catch (error) {
      console.error(`Import failed for ${locale}:`, error);
      return false;
    }
  }

  /**
   * Load locale data dynamically
   */
  private async loadLocaleData(locale: string): Promise<boolean> {
    const angularLocale = this.getAngularLocale(locale);

    // If already loaded, return true
    if (this.loadedLocales.has(angularLocale)) {
      console.log(`Locale ${angularLocale} already loaded`);
      return true;
    }

    try {
      console.log(`Attempting to load locale: ${angularLocale}`);

      // Use a comprehensive import approach with all available Angular locales
      let data;
      switch (angularLocale) {
        case 'af': data = await import('@angular/common/locales/af'); break;
        case 'af-NA': data = await import('@angular/common/locales/af-NA'); break;
        case 'agq': data = await import('@angular/common/locales/agq'); break;
        case 'ak': data = await import('@angular/common/locales/ak'); break;
        case 'am': data = await import('@angular/common/locales/am'); break;
        case 'ar': data = await import('@angular/common/locales/ar'); break;
        case 'ar-AE': data = await import('@angular/common/locales/ar-AE'); break;
        case 'ar-BH': data = await import('@angular/common/locales/ar-BH'); break;
        case 'ar-DJ': data = await import('@angular/common/locales/ar-DJ'); break;
        case 'ar-DZ': data = await import('@angular/common/locales/ar-DZ'); break;
        case 'ar-EG': data = await import('@angular/common/locales/ar-EG'); break;
        case 'ar-EH': data = await import('@angular/common/locales/ar-EH'); break;
        case 'ar-ER': data = await import('@angular/common/locales/ar-ER'); break;
        case 'ar-IL': data = await import('@angular/common/locales/ar-IL'); break;
        case 'ar-IQ': data = await import('@angular/common/locales/ar-IQ'); break;
        case 'ar-JO': data = await import('@angular/common/locales/ar-JO'); break;
        case 'ar-KM': data = await import('@angular/common/locales/ar-KM'); break;
        case 'ar-KW': data = await import('@angular/common/locales/ar-KW'); break;
        case 'ar-LB': data = await import('@angular/common/locales/ar-LB'); break;
        case 'ar-LY': data = await import('@angular/common/locales/ar-LY'); break;
        case 'ar-MA': data = await import('@angular/common/locales/ar-MA'); break;
        case 'ar-MR': data = await import('@angular/common/locales/ar-MR'); break;
        case 'ar-OM': data = await import('@angular/common/locales/ar-OM'); break;
        case 'ar-PS': data = await import('@angular/common/locales/ar-PS'); break;
        case 'ar-QA': data = await import('@angular/common/locales/ar-QA'); break;
        case 'ar-SA': data = await import('@angular/common/locales/ar-SA'); break;
        case 'ar-SD': data = await import('@angular/common/locales/ar-SD'); break;
        case 'ar-SO': data = await import('@angular/common/locales/ar-SO'); break;
        case 'ar-SS': data = await import('@angular/common/locales/ar-SS'); break;
        case 'ar-SY': data = await import('@angular/common/locales/ar-SY'); break;
        case 'ar-TD': data = await import('@angular/common/locales/ar-TD'); break;
        case 'ar-TN': data = await import('@angular/common/locales/ar-TN'); break;
        case 'ar-YE': data = await import('@angular/common/locales/ar-YE'); break;
        case 'as': data = await import('@angular/common/locales/as'); break;
        case 'asa': data = await import('@angular/common/locales/asa'); break;
        case 'ast': data = await import('@angular/common/locales/ast'); break;
        case 'az': data = await import('@angular/common/locales/az'); break;
        case 'az-Cyrl': data = await import('@angular/common/locales/az-Cyrl'); break;
        case 'az-Latn': data = await import('@angular/common/locales/az-Latn'); break;
        case 'bas': data = await import('@angular/common/locales/bas'); break;
        case 'be': data = await import('@angular/common/locales/be'); break;
        case 'be-tarask': data = await import('@angular/common/locales/be-tarask'); break;
        case 'bem': data = await import('@angular/common/locales/bem'); break;
        case 'bez': data = await import('@angular/common/locales/bez'); break;
        case 'bg': data = await import('@angular/common/locales/bg'); break;
        case 'bm': data = await import('@angular/common/locales/bm'); break;
        case 'bn': data = await import('@angular/common/locales/bn'); break;
        case 'bn-IN': data = await import('@angular/common/locales/bn-IN'); break;
        case 'bo': data = await import('@angular/common/locales/bo'); break;
        case 'bo-IN': data = await import('@angular/common/locales/bo-IN'); break;
        case 'br': data = await import('@angular/common/locales/br'); break;
        case 'brx': data = await import('@angular/common/locales/brx'); break;
        case 'bs': data = await import('@angular/common/locales/bs'); break;
        case 'bs-Cyrl': data = await import('@angular/common/locales/bs-Cyrl'); break;
        case 'bs-Latn': data = await import('@angular/common/locales/bs-Latn'); break;
        case 'ca': data = await import('@angular/common/locales/ca'); break;
        case 'ca-AD': data = await import('@angular/common/locales/ca-AD'); break;
        case 'ca-ES-valencia': data = await import('@angular/common/locales/ca-ES-valencia'); break;
        case 'ca-FR': data = await import('@angular/common/locales/ca-FR'); break;
        case 'ca-IT': data = await import('@angular/common/locales/ca-IT'); break;
        case 'ccp': data = await import('@angular/common/locales/ccp'); break;
        case 'ccp-IN': data = await import('@angular/common/locales/ccp-IN'); break;
        case 'ce': data = await import('@angular/common/locales/ce'); break;
        case 'ceb': data = await import('@angular/common/locales/ceb'); break;
        case 'cgg': data = await import('@angular/common/locales/cgg'); break;
        case 'chr': data = await import('@angular/common/locales/chr'); break;
        case 'ckb': data = await import('@angular/common/locales/ckb'); break;
        case 'ckb-IR': data = await import('@angular/common/locales/ckb-IR'); break;
        case 'cs': data = await import('@angular/common/locales/cs'); break;
        case 'cy': data = await import('@angular/common/locales/cy'); break;
        case 'da': data = await import('@angular/common/locales/da'); break;
        case 'da-GL': data = await import('@angular/common/locales/da-GL'); break;
        case 'dav': data = await import('@angular/common/locales/dav'); break;
        case 'de': data = await import('@angular/common/locales/de'); break;
        case 'de-AT': data = await import('@angular/common/locales/de-AT'); break;
        case 'de-BE': data = await import('@angular/common/locales/de-BE'); break;
        case 'de-CH': data = await import('@angular/common/locales/de-CH'); break;
        case 'de-IT': data = await import('@angular/common/locales/de-IT'); break;
        case 'de-LI': data = await import('@angular/common/locales/de-LI'); break;
        case 'de-LU': data = await import('@angular/common/locales/de-LU'); break;
        case 'dje': data = await import('@angular/common/locales/dje'); break;
        case 'doi': data = await import('@angular/common/locales/doi'); break;
        case 'dsb': data = await import('@angular/common/locales/dsb'); break;
        case 'dua': data = await import('@angular/common/locales/dua'); break;
        case 'dyo': data = await import('@angular/common/locales/dyo'); break;
        case 'dz': data = await import('@angular/common/locales/dz'); break;
        case 'ebu': data = await import('@angular/common/locales/ebu'); break;
        case 'ee': data = await import('@angular/common/locales/ee'); break;
        case 'ee-TG': data = await import('@angular/common/locales/ee-TG'); break;
        case 'el': data = await import('@angular/common/locales/el'); break;
        case 'el-CY': data = await import('@angular/common/locales/el-CY'); break;
        case 'en': data = await import('@angular/common/locales/en'); break;
        case 'en-001': data = await import('@angular/common/locales/en-001'); break;
        case 'en-150': data = await import('@angular/common/locales/en-150'); break;
        case 'en-AE': data = await import('@angular/common/locales/en-AE'); break;
        case 'en-AG': data = await import('@angular/common/locales/en-AG'); break;
        case 'en-AI': data = await import('@angular/common/locales/en-AI'); break;
        case 'en-AS': data = await import('@angular/common/locales/en-AS'); break;
        case 'en-AT': data = await import('@angular/common/locales/en-AT'); break;
        case 'en-AU': data = await import('@angular/common/locales/en-AU'); break;
        case 'en-BB': data = await import('@angular/common/locales/en-BB'); break;
        case 'en-BE': data = await import('@angular/common/locales/en-BE'); break;
        case 'en-BI': data = await import('@angular/common/locales/en-BI'); break;
        case 'en-BM': data = await import('@angular/common/locales/en-BM'); break;
        case 'en-BS': data = await import('@angular/common/locales/en-BS'); break;
        case 'en-BW': data = await import('@angular/common/locales/en-BW'); break;
        case 'en-BZ': data = await import('@angular/common/locales/en-BZ'); break;
        case 'en-CA': data = await import('@angular/common/locales/en-CA'); break;
        case 'en-CC': data = await import('@angular/common/locales/en-CC'); break;
        case 'en-CH': data = await import('@angular/common/locales/en-CH'); break;
        case 'en-CK': data = await import('@angular/common/locales/en-CK'); break;
        case 'en-CM': data = await import('@angular/common/locales/en-CM'); break;
        case 'en-CX': data = await import('@angular/common/locales/en-CX'); break;
        case 'en-CY': data = await import('@angular/common/locales/en-CY'); break;
        case 'en-DE': data = await import('@angular/common/locales/en-DE'); break;
        case 'en-DG': data = await import('@angular/common/locales/en-DG'); break;
        case 'en-DK': data = await import('@angular/common/locales/en-DK'); break;
        case 'en-DM': data = await import('@angular/common/locales/en-DM'); break;
        case 'en-ER': data = await import('@angular/common/locales/en-ER'); break;
        case 'en-FI': data = await import('@angular/common/locales/en-FI'); break;
        case 'en-FJ': data = await import('@angular/common/locales/en-FJ'); break;
        case 'en-FK': data = await import('@angular/common/locales/en-FK'); break;
        case 'en-FM': data = await import('@angular/common/locales/en-FM'); break;
        case 'en-GB': data = await import('@angular/common/locales/en-GB'); break;
        case 'en-GD': data = await import('@angular/common/locales/en-GD'); break;
        case 'en-GG': data = await import('@angular/common/locales/en-GG'); break;
        case 'en-GH': data = await import('@angular/common/locales/en-GH'); break;
        case 'en-GI': data = await import('@angular/common/locales/en-GI'); break;
        case 'en-GM': data = await import('@angular/common/locales/en-GM'); break;
        case 'en-GU': data = await import('@angular/common/locales/en-GU'); break;
        case 'en-GY': data = await import('@angular/common/locales/en-GY'); break;
        case 'en-HK': data = await import('@angular/common/locales/en-HK'); break;
        case 'en-IE': data = await import('@angular/common/locales/en-IE'); break;
        case 'en-IL': data = await import('@angular/common/locales/en-IL'); break;
        case 'en-IM': data = await import('@angular/common/locales/en-IM'); break;
        case 'en-IN': data = await import('@angular/common/locales/en-IN'); break;
        case 'en-IO': data = await import('@angular/common/locales/en-IO'); break;
        case 'en-JE': data = await import('@angular/common/locales/en-JE'); break;
        case 'en-JM': data = await import('@angular/common/locales/en-JM'); break;
        case 'en-KE': data = await import('@angular/common/locales/en-KE'); break;
        case 'en-KI': data = await import('@angular/common/locales/en-KI'); break;
        case 'en-KN': data = await import('@angular/common/locales/en-KN'); break;
        case 'en-KY': data = await import('@angular/common/locales/en-KY'); break;
        case 'en-LC': data = await import('@angular/common/locales/en-LC'); break;
        case 'en-LR': data = await import('@angular/common/locales/en-LR'); break;
        case 'en-LS': data = await import('@angular/common/locales/en-LS'); break;
        case 'en-MG': data = await import('@angular/common/locales/en-MG'); break;
        case 'en-MH': data = await import('@angular/common/locales/en-MH'); break;
        case 'en-MO': data = await import('@angular/common/locales/en-MO'); break;
        case 'en-MP': data = await import('@angular/common/locales/en-MP'); break;
        case 'en-MS': data = await import('@angular/common/locales/en-MS'); break;
        case 'en-MT': data = await import('@angular/common/locales/en-MT'); break;
        case 'en-MU': data = await import('@angular/common/locales/en-MU'); break;
        case 'en-MV': data = await import('@angular/common/locales/en-MV'); break;
        case 'en-MW': data = await import('@angular/common/locales/en-MW'); break;
        case 'en-MY': data = await import('@angular/common/locales/en-MY'); break;
        case 'en-NA': data = await import('@angular/common/locales/en-NA'); break;
        case 'en-NF': data = await import('@angular/common/locales/en-NF'); break;
        case 'en-NG': data = await import('@angular/common/locales/en-NG'); break;
        case 'en-NL': data = await import('@angular/common/locales/en-NL'); break;
        case 'en-NR': data = await import('@angular/common/locales/en-NR'); break;
        case 'en-NU': data = await import('@angular/common/locales/en-NU'); break;
        case 'en-NZ': data = await import('@angular/common/locales/en-NZ'); break;
        case 'en-PG': data = await import('@angular/common/locales/en-PG'); break;
        case 'en-PH': data = await import('@angular/common/locales/en-PH'); break;
        case 'en-PK': data = await import('@angular/common/locales/en-PK'); break;
        case 'en-PN': data = await import('@angular/common/locales/en-PN'); break;
        case 'en-PR': data = await import('@angular/common/locales/en-PR'); break;
        case 'en-PW': data = await import('@angular/common/locales/en-PW'); break;
        case 'en-RW': data = await import('@angular/common/locales/en-RW'); break;
        case 'en-SB': data = await import('@angular/common/locales/en-SB'); break;
        case 'en-SC': data = await import('@angular/common/locales/en-SC'); break;
        case 'en-SD': data = await import('@angular/common/locales/en-SD'); break;
        case 'en-SE': data = await import('@angular/common/locales/en-SE'); break;
        case 'en-SG': data = await import('@angular/common/locales/en-SG'); break;
        case 'en-SH': data = await import('@angular/common/locales/en-SH'); break;
        case 'en-SI': data = await import('@angular/common/locales/en-SI'); break;
        case 'en-SL': data = await import('@angular/common/locales/en-SL'); break;
        case 'en-SS': data = await import('@angular/common/locales/en-SS'); break;
        case 'en-SX': data = await import('@angular/common/locales/en-SX'); break;
        case 'en-SZ': data = await import('@angular/common/locales/en-SZ'); break;
        case 'en-TC': data = await import('@angular/common/locales/en-TC'); break;
        case 'en-TK': data = await import('@angular/common/locales/en-TK'); break;
        case 'en-TO': data = await import('@angular/common/locales/en-TO'); break;
        case 'en-TT': data = await import('@angular/common/locales/en-TT'); break;
        case 'en-TV': data = await import('@angular/common/locales/en-TV'); break;
        case 'en-TZ': data = await import('@angular/common/locales/en-TZ'); break;
        case 'en-UG': data = await import('@angular/common/locales/en-UG'); break;
        case 'en-UM': data = await import('@angular/common/locales/en-UM'); break;
        case 'en-VC': data = await import('@angular/common/locales/en-VC'); break;
        case 'en-VG': data = await import('@angular/common/locales/en-VG'); break;
        case 'en-VI': data = await import('@angular/common/locales/en-VI'); break;
        case 'en-VU': data = await import('@angular/common/locales/en-VU'); break;
        case 'en-WS': data = await import('@angular/common/locales/en-WS'); break;
        case 'en-ZA': data = await import('@angular/common/locales/en-ZA'); break;
        case 'en-ZM': data = await import('@angular/common/locales/en-ZM'); break;
        case 'en-ZW': data = await import('@angular/common/locales/en-ZW'); break;
        case 'eo': data = await import('@angular/common/locales/eo'); break;
        case 'es': data = await import('@angular/common/locales/es'); break;
        case 'es-419': data = await import('@angular/common/locales/es-419'); break;
        case 'es-AR': data = await import('@angular/common/locales/es-AR'); break;
        case 'es-BO': data = await import('@angular/common/locales/es-BO'); break;
        case 'es-BR': data = await import('@angular/common/locales/es-BR'); break;
        case 'es-BZ': data = await import('@angular/common/locales/es-BZ'); break;
        case 'es-CL': data = await import('@angular/common/locales/es-CL'); break;
        case 'es-CO': data = await import('@angular/common/locales/es-CO'); break;
        case 'es-CR': data = await import('@angular/common/locales/es-CR'); break;
        case 'es-CU': data = await import('@angular/common/locales/es-CU'); break;
        case 'es-DO': data = await import('@angular/common/locales/es-DO'); break;
        case 'es-EA': data = await import('@angular/common/locales/es-EA'); break;
        case 'es-EC': data = await import('@angular/common/locales/es-EC'); break;
        case 'es-GQ': data = await import('@angular/common/locales/es-GQ'); break;
        case 'es-GT': data = await import('@angular/common/locales/es-GT'); break;
        case 'es-HN': data = await import('@angular/common/locales/es-HN'); break;
        case 'es-IC': data = await import('@angular/common/locales/es-IC'); break;
        case 'es-MX': data = await import('@angular/common/locales/es-MX'); break;
        case 'es-NI': data = await import('@angular/common/locales/es-NI'); break;
        case 'es-PA': data = await import('@angular/common/locales/es-PA'); break;
        case 'es-PE': data = await import('@angular/common/locales/es-PE'); break;
        case 'es-PH': data = await import('@angular/common/locales/es-PH'); break;
        case 'es-PR': data = await import('@angular/common/locales/es-PR'); break;
        case 'es-PY': data = await import('@angular/common/locales/es-PY'); break;
        case 'es-SV': data = await import('@angular/common/locales/es-SV'); break;
        case 'es-US': data = await import('@angular/common/locales/es-US'); break;
        case 'es-UY': data = await import('@angular/common/locales/es-UY'); break;
        case 'es-VE': data = await import('@angular/common/locales/es-VE'); break;
        case 'et': data = await import('@angular/common/locales/et'); break;
        case 'eu': data = await import('@angular/common/locales/eu'); break;
        case 'ewo': data = await import('@angular/common/locales/ewo'); break;
        case 'fa': data = await import('@angular/common/locales/fa'); break;
        case 'fa-AF': data = await import('@angular/common/locales/fa-AF'); break;
        case 'ff': data = await import('@angular/common/locales/ff'); break;
        case 'ff-Adlm': data = await import('@angular/common/locales/ff-Adlm'); break;
        case 'ff-Adlm-BF': data = await import('@angular/common/locales/ff-Adlm-BF'); break;
        case 'ff-Adlm-CM': data = await import('@angular/common/locales/ff-Adlm-CM'); break;
        case 'ff-Adlm-GH': data = await import('@angular/common/locales/ff-Adlm-GH'); break;
        case 'ff-Adlm-GM': data = await import('@angular/common/locales/ff-Adlm-GM'); break;
        case 'ff-Adlm-GW': data = await import('@angular/common/locales/ff-Adlm-GW'); break;
        case 'ff-Adlm-LR': data = await import('@angular/common/locales/ff-Adlm-LR'); break;
        case 'ff-Adlm-MR': data = await import('@angular/common/locales/ff-Adlm-MR'); break;
        case 'ff-Adlm-NE': data = await import('@angular/common/locales/ff-Adlm-NE'); break;
        case 'ff-Adlm-NG': data = await import('@angular/common/locales/ff-Adlm-NG'); break;
        case 'ff-Adlm-SL': data = await import('@angular/common/locales/ff-Adlm-SL'); break;
        case 'ff-Adlm-SN': data = await import('@angular/common/locales/ff-Adlm-SN'); break;
        case 'ff-CM': data = await import('@angular/common/locales/ff-CM'); break;
        case 'ff-GN': data = await import('@angular/common/locales/ff-GN'); break;
        case 'ff-Latn': data = await import('@angular/common/locales/ff-Latn'); break;
        case 'ff-Latn-BF': data = await import('@angular/common/locales/ff-Latn-BF'); break;
        case 'ff-Latn-CM': data = await import('@angular/common/locales/ff-Latn-CM'); break;
        case 'ff-Latn-GH': data = await import('@angular/common/locales/ff-Latn-GH'); break;
        case 'ff-Latn-GM': data = await import('@angular/common/locales/ff-Latn-GM'); break;
        case 'ff-Latn-GN': data = await import('@angular/common/locales/ff-Latn-GN'); break;
        case 'ff-Latn-GW': data = await import('@angular/common/locales/ff-Latn-GW'); break;
        case 'ff-Latn-LR': data = await import('@angular/common/locales/ff-Latn-LR'); break;
        case 'ff-Latn-MR': data = await import('@angular/common/locales/ff-Latn-MR'); break;
        case 'ff-Latn-NE': data = await import('@angular/common/locales/ff-Latn-NE'); break;
        case 'ff-Latn-NG': data = await import('@angular/common/locales/ff-Latn-NG'); break;
        case 'ff-Latn-SL': data = await import('@angular/common/locales/ff-Latn-SL'); break;
        case 'ff-MR': data = await import('@angular/common/locales/ff-MR'); break;
        case 'fi': data = await import('@angular/common/locales/fi'); break;
        case 'fil': data = await import('@angular/common/locales/fil'); break;
        case 'fo': data = await import('@angular/common/locales/fo'); break;
        case 'fo-DK': data = await import('@angular/common/locales/fo-DK'); break;
        case 'fr': data = await import('@angular/common/locales/fr'); break;
        case 'fr-BE': data = await import('@angular/common/locales/fr-BE'); break;
        case 'fr-BF': data = await import('@angular/common/locales/fr-BF'); break;
        case 'fr-BI': data = await import('@angular/common/locales/fr-BI'); break;
        case 'fr-BJ': data = await import('@angular/common/locales/fr-BJ'); break;
        case 'fr-BL': data = await import('@angular/common/locales/fr-BL'); break;
        case 'fr-CA': data = await import('@angular/common/locales/fr-CA'); break;
        case 'fr-CD': data = await import('@angular/common/locales/fr-CD'); break;
        case 'fr-CF': data = await import('@angular/common/locales/fr-CF'); break;
        case 'fr-CG': data = await import('@angular/common/locales/fr-CG'); break;
        case 'fr-CH': data = await import('@angular/common/locales/fr-CH'); break;
        case 'fr-CI': data = await import('@angular/common/locales/fr-CI'); break;
        case 'fr-CM': data = await import('@angular/common/locales/fr-CM'); break;
        case 'fr-DJ': data = await import('@angular/common/locales/fr-DJ'); break;
        case 'fr-DZ': data = await import('@angular/common/locales/fr-DZ'); break;
        case 'fr-GA': data = await import('@angular/common/locales/fr-GA'); break;
        case 'fr-GF': data = await import('@angular/common/locales/fr-GF'); break;
        case 'fr-GN': data = await import('@angular/common/locales/fr-GN'); break;
        case 'fr-GP': data = await import('@angular/common/locales/fr-GP'); break;
        case 'fr-GQ': data = await import('@angular/common/locales/fr-GQ'); break;
        case 'fr-HT': data = await import('@angular/common/locales/fr-HT'); break;
        case 'fr-KM': data = await import('@angular/common/locales/fr-KM'); break;
        case 'fr-LU': data = await import('@angular/common/locales/fr-LU'); break;
        case 'fr-MA': data = await import('@angular/common/locales/fr-MA'); break;
        case 'fr-MC': data = await import('@angular/common/locales/fr-MC'); break;
        case 'fr-MF': data = await import('@angular/common/locales/fr-MF'); break;
        case 'fr-MG': data = await import('@angular/common/locales/fr-MG'); break;
        case 'fr-ML': data = await import('@angular/common/locales/fr-ML'); break;
        case 'fr-MQ': data = await import('@angular/common/locales/fr-MQ'); break;
        case 'fr-MR': data = await import('@angular/common/locales/fr-MR'); break;
        case 'fr-MU': data = await import('@angular/common/locales/fr-MU'); break;
        case 'fr-NC': data = await import('@angular/common/locales/fr-NC'); break;
        case 'fr-NE': data = await import('@angular/common/locales/fr-NE'); break;
        case 'fr-PF': data = await import('@angular/common/locales/fr-PF'); break;
        case 'fr-PM': data = await import('@angular/common/locales/fr-PM'); break;
        case 'fr-RE': data = await import('@angular/common/locales/fr-RE'); break;
        case 'fr-RW': data = await import('@angular/common/locales/fr-RW'); break;
        case 'fr-SC': data = await import('@angular/common/locales/fr-SC'); break;
        case 'fr-SN': data = await import('@angular/common/locales/fr-SN'); break;
        case 'fr-SY': data = await import('@angular/common/locales/fr-SY'); break;
        case 'fr-TD': data = await import('@angular/common/locales/fr-TD'); break;
        case 'fr-TG': data = await import('@angular/common/locales/fr-TG'); break;
        case 'fr-TN': data = await import('@angular/common/locales/fr-TN'); break;
        case 'fr-VU': data = await import('@angular/common/locales/fr-VU'); break;
        case 'fr-WF': data = await import('@angular/common/locales/fr-WF'); break;
        case 'fr-YT': data = await import('@angular/common/locales/fr-YT'); break;
        case 'fur': data = await import('@angular/common/locales/fur'); break;
        case 'fy': data = await import('@angular/common/locales/fy'); break;
        case 'ga': data = await import('@angular/common/locales/ga'); break;
        case 'ga-GB': data = await import('@angular/common/locales/ga-GB'); break;
        case 'gd': data = await import('@angular/common/locales/gd'); break;
        case 'gl': data = await import('@angular/common/locales/gl'); break;
        case 'gsw': data = await import('@angular/common/locales/gsw'); break;
        case 'gsw-FR': data = await import('@angular/common/locales/gsw-FR'); break;
        case 'gsw-LI': data = await import('@angular/common/locales/gsw-LI'); break;
        case 'gu': data = await import('@angular/common/locales/gu'); break;
        case 'guz': data = await import('@angular/common/locales/guz'); break;
        case 'gv': data = await import('@angular/common/locales/gv'); break;
        case 'ha': data = await import('@angular/common/locales/ha'); break;
        case 'ha-GH': data = await import('@angular/common/locales/ha-GH'); break;
        case 'ha-NE': data = await import('@angular/common/locales/ha-NE'); break;
        case 'haw': data = await import('@angular/common/locales/haw'); break;
        case 'he': data = await import('@angular/common/locales/he'); break;
        case 'hi': data = await import('@angular/common/locales/hi'); break;
        case 'hi-Latn': data = await import('@angular/common/locales/hi-Latn'); break;
        case 'hr': data = await import('@angular/common/locales/hr'); break;
        case 'hr-BA': data = await import('@angular/common/locales/hr-BA'); break;
        case 'hsb': data = await import('@angular/common/locales/hsb'); break;
        case 'hu': data = await import('@angular/common/locales/hu'); break;
        case 'hy': data = await import('@angular/common/locales/hy'); break;
        case 'ia': data = await import('@angular/common/locales/ia'); break;
        case 'id': data = await import('@angular/common/locales/id'); break;
        case 'ig': data = await import('@angular/common/locales/ig'); break;
        case 'ii': data = await import('@angular/common/locales/ii'); break;
        case 'is': data = await import('@angular/common/locales/is'); break;
        case 'it': data = await import('@angular/common/locales/it'); break;
        case 'it-CH': data = await import('@angular/common/locales/it-CH'); break;
        case 'it-SM': data = await import('@angular/common/locales/it-SM'); break;
        case 'it-VA': data = await import('@angular/common/locales/it-VA'); break;
        case 'ja': data = await import('@angular/common/locales/ja'); break;
        case 'jgo': data = await import('@angular/common/locales/jgo'); break;
        case 'jmc': data = await import('@angular/common/locales/jmc'); break;
        case 'jv': data = await import('@angular/common/locales/jv'); break;
        case 'ka': data = await import('@angular/common/locales/ka'); break;
        case 'kab': data = await import('@angular/common/locales/kab'); break;
        case 'kam': data = await import('@angular/common/locales/kam'); break;
        case 'kde': data = await import('@angular/common/locales/kde'); break;
        case 'kea': data = await import('@angular/common/locales/kea'); break;
        case 'kgp': data = await import('@angular/common/locales/kgp'); break;
        case 'khq': data = await import('@angular/common/locales/khq'); break;
        case 'ki': data = await import('@angular/common/locales/ki'); break;
        case 'kk': data = await import('@angular/common/locales/kk'); break;
        case 'kkj': data = await import('@angular/common/locales/kkj'); break;
        case 'kl': data = await import('@angular/common/locales/kl'); break;
        case 'kln': data = await import('@angular/common/locales/kln'); break;
        case 'km': data = await import('@angular/common/locales/km'); break;
        case 'kn': data = await import('@angular/common/locales/kn'); break;
        case 'ko': data = await import('@angular/common/locales/ko'); break;
        case 'ko-KP': data = await import('@angular/common/locales/ko-KP'); break;
        case 'kok': data = await import('@angular/common/locales/kok'); break;
        case 'ks': data = await import('@angular/common/locales/ks'); break;
        case 'ks-Arab': data = await import('@angular/common/locales/ks-Arab'); break;
        case 'ks-Deva': data = await import('@angular/common/locales/ks-Deva'); break;
        case 'ksb': data = await import('@angular/common/locales/ksb'); break;
        case 'ksf': data = await import('@angular/common/locales/ksf'); break;
        case 'ksh': data = await import('@angular/common/locales/ksh'); break;
        case 'ku': data = await import('@angular/common/locales/ku'); break;
        case 'kw': data = await import('@angular/common/locales/kw'); break;
        case 'ky': data = await import('@angular/common/locales/ky'); break;
        case 'lag': data = await import('@angular/common/locales/lag'); break;
        case 'lb': data = await import('@angular/common/locales/lb'); break;
        case 'lg': data = await import('@angular/common/locales/lg'); break;
        case 'lkt': data = await import('@angular/common/locales/lkt'); break;
        case 'ln': data = await import('@angular/common/locales/ln'); break;
        case 'ln-AO': data = await import('@angular/common/locales/ln-AO'); break;
        case 'ln-CF': data = await import('@angular/common/locales/ln-CF'); break;
        case 'ln-CG': data = await import('@angular/common/locales/ln-CG'); break;
        case 'lo': data = await import('@angular/common/locales/lo'); break;
        case 'lrc': data = await import('@angular/common/locales/lrc'); break;
        case 'lrc-IQ': data = await import('@angular/common/locales/lrc-IQ'); break;
        case 'lt': data = await import('@angular/common/locales/lt'); break;
        case 'lu': data = await import('@angular/common/locales/lu'); break;
        case 'luo': data = await import('@angular/common/locales/luo'); break;
        case 'luy': data = await import('@angular/common/locales/luy'); break;
        case 'lv': data = await import('@angular/common/locales/lv'); break;
        case 'mai': data = await import('@angular/common/locales/mai'); break;
        case 'mas': data = await import('@angular/common/locales/mas'); break;
        case 'mas-TZ': data = await import('@angular/common/locales/mas-TZ'); break;
        case 'mer': data = await import('@angular/common/locales/mer'); break;
        case 'mfe': data = await import('@angular/common/locales/mfe'); break;
        case 'mg': data = await import('@angular/common/locales/mg'); break;
        case 'mgh': data = await import('@angular/common/locales/mgh'); break;
        case 'mgo': data = await import('@angular/common/locales/mgo'); break;
        case 'mi': data = await import('@angular/common/locales/mi'); break;
        case 'mk': data = await import('@angular/common/locales/mk'); break;
        case 'ml': data = await import('@angular/common/locales/ml'); break;
        case 'mn': data = await import('@angular/common/locales/mn'); break;
        case 'mni': data = await import('@angular/common/locales/mni'); break;
        case 'mni-Beng': data = await import('@angular/common/locales/mni-Beng'); break;
        case 'mr': data = await import('@angular/common/locales/mr'); break;
        case 'ms': data = await import('@angular/common/locales/ms'); break;
        case 'ms-BN': data = await import('@angular/common/locales/ms-BN'); break;
        case 'ms-ID': data = await import('@angular/common/locales/ms-ID'); break;
        case 'ms-SG': data = await import('@angular/common/locales/ms-SG'); break;
        case 'mt': data = await import('@angular/common/locales/mt'); break;
        case 'mua': data = await import('@angular/common/locales/mua'); break;
        case 'my': data = await import('@angular/common/locales/my'); break;
        case 'mzn': data = await import('@angular/common/locales/mzn'); break;
        case 'naq': data = await import('@angular/common/locales/naq'); break;
        case 'nb': data = await import('@angular/common/locales/nb'); break;
        case 'nb-SJ': data = await import('@angular/common/locales/nb-SJ'); break;
        case 'nd': data = await import('@angular/common/locales/nd'); break;
        case 'nds': data = await import('@angular/common/locales/nds'); break;
        case 'nds-NL': data = await import('@angular/common/locales/nds-NL'); break;
        case 'ne': data = await import('@angular/common/locales/ne'); break;
        case 'ne-IN': data = await import('@angular/common/locales/ne-IN'); break;
        case 'nl': data = await import('@angular/common/locales/nl'); break;
        case 'nl-AW': data = await import('@angular/common/locales/nl-AW'); break;
        case 'nl-BE': data = await import('@angular/common/locales/nl-BE'); break;
        case 'nl-BQ': data = await import('@angular/common/locales/nl-BQ'); break;
        case 'nl-CW': data = await import('@angular/common/locales/nl-CW'); break;
        case 'nl-SR': data = await import('@angular/common/locales/nl-SR'); break;
        case 'nl-SX': data = await import('@angular/common/locales/nl-SX'); break;
        case 'nmg': data = await import('@angular/common/locales/nmg'); break;
        case 'nn': data = await import('@angular/common/locales/nn'); break;
        case 'nnh': data = await import('@angular/common/locales/nnh'); break;
        case 'no': data = await import('@angular/common/locales/no'); break;
        case 'nus': data = await import('@angular/common/locales/nus'); break;
        case 'nyn': data = await import('@angular/common/locales/nyn'); break;
        case 'om': data = await import('@angular/common/locales/om'); break;
        case 'om-KE': data = await import('@angular/common/locales/om-KE'); break;
        case 'or': data = await import('@angular/common/locales/or'); break;
        case 'os': data = await import('@angular/common/locales/os'); break;
        case 'os-RU': data = await import('@angular/common/locales/os-RU'); break;
        case 'pa': data = await import('@angular/common/locales/pa'); break;
        case 'pa-Arab': data = await import('@angular/common/locales/pa-Arab'); break;
        case 'pa-Guru': data = await import('@angular/common/locales/pa-Guru'); break;
        case 'pcm': data = await import('@angular/common/locales/pcm'); break;
        case 'pl': data = await import('@angular/common/locales/pl'); break;
        case 'ps': data = await import('@angular/common/locales/ps'); break;
        case 'ps-PK': data = await import('@angular/common/locales/ps-PK'); break;
        case 'pt': data = await import('@angular/common/locales/pt'); break;
        case 'pt-AO': data = await import('@angular/common/locales/pt-AO'); break;
        case 'pt-CH': data = await import('@angular/common/locales/pt-CH'); break;
        case 'pt-CV': data = await import('@angular/common/locales/pt-CV'); break;
        case 'pt-GQ': data = await import('@angular/common/locales/pt-GQ'); break;
        case 'pt-GW': data = await import('@angular/common/locales/pt-GW'); break;
        case 'pt-LU': data = await import('@angular/common/locales/pt-LU'); break;
        case 'pt-MO': data = await import('@angular/common/locales/pt-MO'); break;
        case 'pt-MZ': data = await import('@angular/common/locales/pt-MZ'); break;
        case 'pt-PT': data = await import('@angular/common/locales/pt-PT'); break;
        case 'pt-ST': data = await import('@angular/common/locales/pt-ST'); break;
        case 'pt-TL': data = await import('@angular/common/locales/pt-TL'); break;
        case 'qu': data = await import('@angular/common/locales/qu'); break;
        case 'qu-BO': data = await import('@angular/common/locales/qu-BO'); break;
        case 'qu-EC': data = await import('@angular/common/locales/qu-EC'); break;
        case 'rm': data = await import('@angular/common/locales/rm'); break;
        case 'rn': data = await import('@angular/common/locales/rn'); break;
        case 'ro': data = await import('@angular/common/locales/ro'); break;
        case 'ro-MD': data = await import('@angular/common/locales/ro-MD'); break;
        case 'rof': data = await import('@angular/common/locales/rof'); break;
        case 'ru': data = await import('@angular/common/locales/ru'); break;
        case 'ru-BY': data = await import('@angular/common/locales/ru-BY'); break;
        case 'ru-KG': data = await import('@angular/common/locales/ru-KG'); break;
        case 'ru-KZ': data = await import('@angular/common/locales/ru-KZ'); break;
        case 'ru-MD': data = await import('@angular/common/locales/ru-MD'); break;
        case 'ru-UA': data = await import('@angular/common/locales/ru-UA'); break;
        case 'rw': data = await import('@angular/common/locales/rw'); break;
        case 'rwk': data = await import('@angular/common/locales/rwk'); break;
        case 'sa': data = await import('@angular/common/locales/sa'); break;
        case 'sah': data = await import('@angular/common/locales/sah'); break;
        case 'saq': data = await import('@angular/common/locales/saq'); break;
        case 'sat': data = await import('@angular/common/locales/sat'); break;
        case 'sat-Olck': data = await import('@angular/common/locales/sat-Olck'); break;
        case 'sbp': data = await import('@angular/common/locales/sbp'); break;
        case 'sc': data = await import('@angular/common/locales/sc'); break;
        case 'sd': data = await import('@angular/common/locales/sd'); break;
        case 'sd-Arab': data = await import('@angular/common/locales/sd-Arab'); break;
        case 'sd-Deva': data = await import('@angular/common/locales/sd-Deva'); break;
        case 'se': data = await import('@angular/common/locales/se'); break;
        case 'se-FI': data = await import('@angular/common/locales/se-FI'); break;
        case 'se-SE': data = await import('@angular/common/locales/se-SE'); break;
        case 'seh': data = await import('@angular/common/locales/seh'); break;
        case 'ses': data = await import('@angular/common/locales/ses'); break;
        case 'sg': data = await import('@angular/common/locales/sg'); break;
        case 'shi': data = await import('@angular/common/locales/shi'); break;
        case 'shi-Latn': data = await import('@angular/common/locales/shi-Latn'); break;
        case 'shi-Tfng': data = await import('@angular/common/locales/shi-Tfng'); break;
        case 'si': data = await import('@angular/common/locales/si'); break;
        case 'sk': data = await import('@angular/common/locales/sk'); break;
        case 'sl': data = await import('@angular/common/locales/sl'); break;
        case 'smn': data = await import('@angular/common/locales/smn'); break;
        case 'sn': data = await import('@angular/common/locales/sn'); break;
        case 'so': data = await import('@angular/common/locales/so'); break;
        case 'so-DJ': data = await import('@angular/common/locales/so-DJ'); break;
        case 'so-ET': data = await import('@angular/common/locales/so-ET'); break;
        case 'so-KE': data = await import('@angular/common/locales/so-KE'); break;
        case 'sq': data = await import('@angular/common/locales/sq'); break;
        case 'sq-MK': data = await import('@angular/common/locales/sq-MK'); break;
        case 'sq-XK': data = await import('@angular/common/locales/sq-XK'); break;
        case 'sr': data = await import('@angular/common/locales/sr'); break;
        case 'sr-Cyrl': data = await import('@angular/common/locales/sr-Cyrl'); break;
        case 'sr-Cyrl-BA': data = await import('@angular/common/locales/sr-Cyrl-BA'); break;
        case 'sr-Cyrl-ME': data = await import('@angular/common/locales/sr-Cyrl-ME'); break;
        case 'sr-Cyrl-XK': data = await import('@angular/common/locales/sr-Cyrl-XK'); break;
        case 'sr-Latn': data = await import('@angular/common/locales/sr-Latn'); break;
        case 'sr-Latn-BA': data = await import('@angular/common/locales/sr-Latn-BA'); break;
        case 'sr-Latn-ME': data = await import('@angular/common/locales/sr-Latn-ME'); break;
        case 'sr-Latn-XK': data = await import('@angular/common/locales/sr-Latn-XK'); break;
        case 'su': data = await import('@angular/common/locales/su'); break;
        case 'su-Latn': data = await import('@angular/common/locales/su-Latn'); break;
        case 'sv': data = await import('@angular/common/locales/sv'); break;
        case 'sv-AX': data = await import('@angular/common/locales/sv-AX'); break;
        case 'sv-FI': data = await import('@angular/common/locales/sv-FI'); break;
        case 'sw': data = await import('@angular/common/locales/sw'); break;
        case 'sw-CD': data = await import('@angular/common/locales/sw-CD'); break;
        case 'sw-KE': data = await import('@angular/common/locales/sw-KE'); break;
        case 'sw-UG': data = await import('@angular/common/locales/sw-UG'); break;
        case 'ta': data = await import('@angular/common/locales/ta'); break;
        case 'ta-LK': data = await import('@angular/common/locales/ta-LK'); break;
        case 'ta-MY': data = await import('@angular/common/locales/ta-MY'); break;
        case 'ta-SG': data = await import('@angular/common/locales/ta-SG'); break;
        case 'te': data = await import('@angular/common/locales/te'); break;
        case 'teo': data = await import('@angular/common/locales/teo'); break;
        case 'teo-KE': data = await import('@angular/common/locales/teo-KE'); break;
        case 'tg': data = await import('@angular/common/locales/tg'); break;
        case 'th': data = await import('@angular/common/locales/th'); break;
        case 'ti': data = await import('@angular/common/locales/ti'); break;
        case 'ti-ER': data = await import('@angular/common/locales/ti-ER'); break;
        case 'tk': data = await import('@angular/common/locales/tk'); break;
        case 'to': data = await import('@angular/common/locales/to'); break;
        case 'tr': data = await import('@angular/common/locales/tr'); break;
        case 'tr-CY': data = await import('@angular/common/locales/tr-CY'); break;
        case 'tt': data = await import('@angular/common/locales/tt'); break;
        case 'twq': data = await import('@angular/common/locales/twq'); break;
        case 'tzm': data = await import('@angular/common/locales/tzm'); break;
        case 'ug': data = await import('@angular/common/locales/ug'); break;
        case 'uk': data = await import('@angular/common/locales/uk'); break;
        case 'und': data = await import('@angular/common/locales/und'); break;
        case 'ur': data = await import('@angular/common/locales/ur'); break;
        case 'ur-IN': data = await import('@angular/common/locales/ur-IN'); break;
        case 'uz': data = await import('@angular/common/locales/uz'); break;
        case 'uz-Arab': data = await import('@angular/common/locales/uz-Arab'); break;
        case 'uz-Cyrl': data = await import('@angular/common/locales/uz-Cyrl'); break;
        case 'uz-Latn': data = await import('@angular/common/locales/uz-Latn'); break;
        case 'vai': data = await import('@angular/common/locales/vai'); break;
        case 'vai-Latn': data = await import('@angular/common/locales/vai-Latn'); break;
        case 'vai-Vaii': data = await import('@angular/common/locales/vai-Vaii'); break;
        case 'vi': data = await import('@angular/common/locales/vi'); break;
        case 'vun': data = await import('@angular/common/locales/vun'); break;
        case 'wae': data = await import('@angular/common/locales/wae'); break;
        case 'wo': data = await import('@angular/common/locales/wo'); break;
        case 'xh': data = await import('@angular/common/locales/xh'); break;
        case 'xog': data = await import('@angular/common/locales/xog'); break;
        case 'yav': data = await import('@angular/common/locales/yav'); break;
        case 'yi': data = await import('@angular/common/locales/yi'); break;
        case 'yo': data = await import('@angular/common/locales/yo'); break;
        case 'yo-BJ': data = await import('@angular/common/locales/yo-BJ'); break;
        case 'yrl': data = await import('@angular/common/locales/yrl'); break;
        case 'yrl-CO': data = await import('@angular/common/locales/yrl-CO'); break;
        case 'yrl-VE': data = await import('@angular/common/locales/yrl-VE'); break;
        case 'yue': data = await import('@angular/common/locales/yue'); break;
        case 'yue-Hans': data = await import('@angular/common/locales/yue-Hans'); break;
        case 'yue-Hant': data = await import('@angular/common/locales/yue-Hant'); break;
        case 'zgh': data = await import('@angular/common/locales/zgh'); break;
        case 'zh': data = await import('@angular/common/locales/zh'); break;
        case 'zh-Hans': data = await import('@angular/common/locales/zh-Hans'); break;
        case 'zh-Hans-HK': data = await import('@angular/common/locales/zh-Hans-HK'); break;
        case 'zh-Hans-MO': data = await import('@angular/common/locales/zh-Hans-MO'); break;
        case 'zh-Hans-SG': data = await import('@angular/common/locales/zh-Hans-SG'); break;
        case 'zh-Hant': data = await import('@angular/common/locales/zh-Hant'); break;
        case 'zh-Hant-HK': data = await import('@angular/common/locales/zh-Hant-HK'); break;
        case 'zh-Hant-MO': data = await import('@angular/common/locales/zh-Hant-MO'); break;
        case 'zu': data = await import('@angular/common/locales/zu'); break;

        default:
          throw new Error(`Locale ${angularLocale} not supported in switch statement`);
      }

      console.log(`Import successful for ${angularLocale}:`, data);

      registerLocaleData(data.default, angularLocale);
      this.loadedLocales.add(angularLocale);
      console.log(`Successfully loaded locale: ${angularLocale}`);
      return true;
    } catch (error) {
      console.warn(`Failed to load locale ${angularLocale}:`, error);

      // Try fallback locale
      const fallbackLocale = this.getFallbackLocale(locale);
      if (fallbackLocale !== locale) {
        console.log(`Trying fallback locale: ${fallbackLocale}`);
        return this.loadLocaleData(fallbackLocale);
      }

      // If no fallback available, load English as last resort
      if (angularLocale !== 'en') {
        console.warn(`Falling back to English locale`);
        return this.loadLocaleData('en');
      }

      console.error(`Failed to load any locale data, including English`);
      return false;
    }
  }

  /**
   * Set the application locale
   */
  async setLocale(locale: string) {
    console.log(`Setting locale to: ${locale}`);

    // Check if the locale is already set
    if (this.current.value === locale) {
      console.log(`Locale ${locale} is already set`);
      return;
    }

    // Load locale data
    const success = await this.loadLocaleData(locale);

    if (success) {
      const angularLocale = this.getAngularLocale(locale);

      // Set moment.js locale
      try {
        moment.locale(locale);
        console.log(`Set moment.js locale to: ${locale}`);
      } catch (error) {
        console.warn(`Failed to set moment locale to ${locale}, using fallback:`, error);
        try {
          moment.locale('en');
        } catch (momentError) {
          console.error('Failed to set moment locale to English:', momentError);
        }
      }

      // Update current locale
      this.current.next(locale);

      // Set lang attribute on html
      this.document.documentElement.lang = locale;

      console.log(`Successfully set locale to: ${locale} (Angular: ${angularLocale})`);
    } else {
      console.error(`Failed to load locale data for: ${locale}`);

      // Even if locale loading fails, try to set the locale to prevent the app from breaking
      try {
        this.current.next(locale);
        this.document.documentElement.lang = locale;
        console.warn(`Set locale to ${locale} without Angular locale data (using fallback formatting)`);
      } catch (error) {
        console.error('Failed to set locale even as fallback:', error);
      }
    }
  }

  /**
   * Get the current locale
   */
  getCurrentLocale(): string {
    return this.current.value;
  }

  /**
   * Get the Angular locale code for the current locale
   */
  getCurrentAngularLocale(): string {
    return this.getAngularLocale(this.current.value);
  }

  /**
   * Check if a locale is supported
   */
  isLocaleSupported(locale: string): boolean {
    return this.LOCALE_MAPPING.hasOwnProperty(locale);
  }

  /**
   * Get all supported locales
   */
  getSupportedLocales(): string[] {
    return Object.keys(this.LOCALE_MAPPING);
  }

  /**
   * Get all loaded locales (for debugging)
   */
  getLoadedLocales(): string[] {
    return Array.from(this.loadedLocales);
  }
}
